import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { StateService } from '@uirouter/core';

import { CimaCore, SessionStorage, Util, XmStore, XmStoreUtil } from 'core/services';
import { StorageToken } from 'core/constants';
import { LocationCollection } from 'store/locations/models/location';
import { Account } from 'store/user/models/account';
import { User } from 'store/user/models/user';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
    column: string;
    direction: SortDirection;
}

const PAGE_SIZE: number = 8;

@Component({
    selector: 'xm-switch-locations',
    styleUrls: ['./switch-locations.scss'],
    templateUrl: './switch-locations.html',
    standalone: false
})
export class XmSwitchLocationsComponent implements OnInit, OnDestroy {
    public locationData: LocationCollection;
    public displayedRows: LocationRow[];
    public selectedAccountGuid: string;
    public currentAccountGuid: string;
    public account: Account;
    public sortState: SortState;
    public showViewMore: boolean;
    public os: string;
    public acc: string;
    public selectedIsOrion: boolean = false;

    private subscriptions: Subscription[] = [];
    private visibleCount: number;
    private xmStore: XmStore;
    private state: StateService;
    private sessionStorage: SessionStorage;
    private cimaCore: CimaCore;

    constructor(xmStore: XmStore, state: StateService, sessionStorage: SessionStorage, cimaCore: CimaCore) {
        Object.assign(this, { xmStore, state, sessionStorage, cimaCore });
    }

    public ngOnInit(): void {
        this.visibleCount = PAGE_SIZE;
        this.sortState = { column: '', direction: 'asc' };
        this.subscriptions.push(XmStoreUtil.subscribe( this.xmStore.peekChild<Account, User>('account', Account, User), (account: Account) => {
            this.account = account;
        }));

        this.currentAccountGuid = this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID);
        if (!this.currentAccountGuid && this.account) {
            this.currentAccountGuid = this.account.authGuid;
        }

        this.subscriptions.push(
            XmStoreUtil.subscribe(
                this.xmStore.peek<LocationCollection>(LocationCollection),
                (data: LocationCollection) => {
                    this.locationData = data;
                    this.updateDisplayedRows();
                }
            )
        );
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public selectLocation(accountGuid: string, account: string, isOrion: boolean): void {
        this.os = isOrion ? account : undefined;
        this.acc = accountGuid;
        this.selectedAccountGuid = isOrion ? account : accountGuid;
        this.selectedIsOrion = isOrion;
    }

    public isSelected(accountGuid: string, account: string, isOrion: boolean): boolean {
        const selectedAccount: string = isOrion ? account : accountGuid;

        return this.selectedAccountGuid === selectedAccount;
    }

    public onCancel(): void {
        this.state.go('account.activity.landing');
    }

    public onSwitch(): void {
        if (!this.selectedAccountGuid) {
            return;
        }

        this.cimaCore.cbmMandateSelectionMode = false;
        // A full page reload to /activity ensures all cached NgRx store data is cleared
        if (this.selectedIsOrion && this.os) {
            this.sessionStorage.set(StorageToken.X_TENANT_ID, 'ORION');
            const locationParams: { os?: string; acc?: string } = {
                ...({ os: this.os }),
                ...(this.state.params.acc && { acc: this.acc })
            };
            window.location.href = this.state.href('account.activity.landing', locationParams);
        } else {
            this.sessionStorage.set(StorageToken.X_TENANT_ID, '');
            const locationParams: { os?: string; acc?: string } = {
                ...(this.state.params.os && { os: undefined }),
                ...({ acc: this.acc })
            };
            window.location.href = this.state.href('account.activity.landing', locationParams);
        }
    }

    public get showCancelCTA(): boolean {
        return !this.cimaCore.cbmMandateSelectionMode;
    }

    public loadMore(): void {
        this.visibleCount += PAGE_SIZE;
        this.updateDisplayedRows();
    }

    public toggleSort(column: string): void {
        if (this.sortState.column === column) {
            this.sortState = {
                column,
                direction: this.sortState.direction === 'asc' ? 'desc' : 'asc'
            };
        } else {
            this.sortState = { column, direction: 'asc' };
        }

        this.sortRows();
        this.updateDisplayedRows();
    }

    protected navigateTo(url: string): void {
        window.location.href = url;
    }

    private sortRows(): void {
        if (!this.locationData?.locationRows || !this.sortState.column) {
            return;
        }

        const col: string = this.sortState.column;
        const dir: number = this.sortState.direction === 'asc' ? 1 : -1;

        this.locationData.locationRows.sort((a: LocationRow, b: LocationRow) => {
            const valA: string = (a[col] || '').toLowerCase();
            const valB: string = (b[col] || '').toLowerCase();

            return valA < valB ? -1 * dir : valA > valB ? 1 * dir : 0;
        });
    }

    private updateDisplayedRows(): void {
        if (!this.locationData?.locationRows) {
            this.displayedRows = [];
            this.showViewMore = false;

            return;
        }

        this.displayedRows = this.locationData.locationRows.slice(0, this.visibleCount);
        this.showViewMore = this.locationData.locationRows.length > this.visibleCount;
    }
}
